import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, from, of, switchMap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CryptoService } from '@/pages/service/core/crypto.service';

/**
 * Encrypts API traffic when the session holds a key (handed out by the backend
 * handshake only while app.encryption.enabled is true). With no key every call
 * passes straight through on plain HTTPS, so the app is unaffected until on.
 *
 * Covers: JSON request/response bodies, the query string (both HttpParams and
 * any query baked into the URL — moved into an encrypted X-EncQ header so the
 * URL carries none), multipart file uploads (each file's bytes are encrypted),
 * and file downloads (the encrypted blob is decrypted back). The URL *path* is
 * never encrypted — the server routes by it.
 *
 * If the backend answers 428/X-Enc-Stale (it restarted and lost the in-memory
 * session key), a fresh key is fetched and the call replayed once.
 */
export const encryptionInterceptor: HttpInterceptorFn = (req, next) => {
  const cryptoSvc = inject(CryptoService);

  // Never wrap the handshake itself — it bootstraps the key.
  if (req.url.includes('/api/crypto/handshake')) return next(req);

  // Only the workflow-definer (apiBaseUrl) carries the crypto layer. The engine
  // (conversationBaseUrl) and the lead/event service have no decrypt filter —
  // an encrypted body would reach their MVC as text/plain garbage (415), so
  // those calls stay plain HTTPS.
  if (!req.url.startsWith(environment.apiBaseUrl)) return next(req);

  // Lazily obtain a key the first time we're logged in (no-op if disabled).
  cryptoSvc.maybeBootstrap();
  if (!cryptoSvc.active) return next(req);

  return send(cryptoSvc, req, next, true);
};

function send(cryptoSvc: CryptoService, req: HttpRequest<any>, next: HttpHandlerFn, mayRetry: boolean): Observable<HttpEvent<any>> {
  return from(buildRequest(cryptoSvc, req)).pipe(
    switchMap(encReq =>
      next(encReq).pipe(
        switchMap(event =>
          event instanceof HttpResponse ? from(decodeResponse(cryptoSvc, event)) : of(event)),
        catchError((err: HttpErrorResponse) => {
          // Stale key after a backend restart: re-handshake and replay once.
          // If the backend now has encryption off, fall back to the plain call.
          if (mayRetry && err.status === 428 && err.headers?.get('X-Enc-Stale') === '1') {
            return from(cryptoSvc.rehandshake()).pipe(
              switchMap(active => active ? send(cryptoSvc, req, next, false) : next(req)));
          }
          if (err.headers?.get('X-Enc') === '1' && typeof err.error === 'string') {
            return from(
              cryptoSvc.decrypt(err.error).then(text => {
                throw new HttpErrorResponse({
                  error: tryParseJson(text), status: err.status, statusText: err.statusText,
                  headers: err.headers, url: err.url ?? undefined,
                });
              }));
          }
          return throwError(() => err);
        })
      ))
  );
}

async function buildRequest(cryptoSvc: CryptoService, req: HttpRequest<any>): Promise<HttpRequest<any>> {
  const sess = cryptoSvc.sessionId!;
  let r = req;

  // 1) Move the whole query string — HttpParams AND any query built straight
  //    into the URL (`...?path=x`) — into an encrypted header, so no request
  //    detail rides in the URL.
  const qIdx = r.urlWithParams.indexOf('?');
  if (qIdx >= 0) {
    const encQ = await cryptoSvc.encrypt(r.urlWithParams.slice(qIdx + 1));
    r = r.clone({ url: r.url.split('?')[0], params: new HttpParams(), setHeaders: { 'X-EncQ': encQ, 'X-Sess': sess } });
  }

  // 2) Multipart upload — encrypt each file part's bytes.
  if (r.body instanceof FormData) {
    const fd = await encryptFormData(cryptoSvc, r.body);
    return r.clone({ body: fd, setHeaders: { 'X-EncF': '1', 'X-Sess': sess }, responseType: 'text' });
  }

  // 3) Binary download — no body; ask the backend to encrypt the response.
  if (r.responseType === 'blob' || r.responseType === 'arraybuffer') {
    return r.clone({ setHeaders: { 'X-Enc': '1', 'X-Sess': sess } });
  }

  // 4) JSON / text request.
  if (r.body != null) {
    const body = await cryptoSvc.encrypt(JSON.stringify(r.body));
    return r.clone({
      body,
      setHeaders: { 'X-Enc': '1', 'X-Sess': sess, 'Content-Type': 'text/plain' },
      responseType: 'text',
    });
  }
  return r.clone({ setHeaders: { 'X-Enc': '1', 'X-Sess': sess }, responseType: 'text' });
}

async function encryptFormData(cryptoSvc: CryptoService, fd: FormData): Promise<FormData> {
  const out = new FormData();
  for (const [k, v] of Array.from(fd.entries())) {
    if (typeof v === 'string') {
      out.append(k, v);
    } else {
      const buf = new Uint8Array(await v.arrayBuffer());
      const enc = await cryptoSvc.encryptBytes(buf);
      out.append(k, new Blob([enc], { type: 'application/octet-stream' }), v.name);
    }
  }
  return out;
}

async function decodeResponse(cryptoSvc: CryptoService, res: HttpResponse<any>): Promise<HttpResponse<any>> {
  const isEnc = res.headers.get('X-Enc') === '1';
  let body = res.body;

  if (body instanceof Blob) {
    if (isEnc) body = new Blob([await cryptoSvc.decryptToBytes(await body.text())]);
    return res.clone({ body });
  }
  if (body instanceof ArrayBuffer) {
    if (isEnc) body = (await cryptoSvc.decryptToBytes(new TextDecoder().decode(body))).buffer;
    return res.clone({ body });
  }
  if (typeof body === 'string') {
    const text = isEnc ? await cryptoSvc.decrypt(body) : body;
    return res.clone({ body: tryParseJson(text) });
  }
  return res.clone({ body });
}

function tryParseJson(text: string): any {
  if (text == null || text === '') return null;
  try { return JSON.parse(text); } catch { return text; }
}
